/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

/**
 * EnrollmentModal - Student class enrollment interface
 * Shows when student has no active cohorts or when requesting additional classes
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription } from "../ui/alert";
import { Users, BookOpen, CheckCircle, Clock, AlertCircle } from "lucide-react";

const EnrollmentModal = ({ open, onOpenChange, userId, isRequired = false }) => {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestingCohortId, setRequestingCohortId] = useState(null);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(null);

  // Fetch available cohorts
  useEffect(() => {
    if (!open) return;

    const fetchCohorts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollment-requests/available-cohorts`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch available cohorts');
        }

        const data = await response.json();
        setCohorts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCohorts();
  }, [open]);

  const handleRequestEnrollment = async (cohortId) => {
    try {
      setRequestingCohortId(cohortId);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollment-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          cohortId,
          message: message.trim() || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit enrollment request');
      }

      setSuccess('Enrollment request submitted! Your teacher will review it shortly.');
      setMessage('');

      // Refresh cohort list to update pending status
      const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollment-requests/available-cohorts`, {
        credentials: 'include'
      });
      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        setCohorts(data);
      }

      // Auto-close success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setRequestingCohortId(null);
    }
  };

  const getCohortStatus = (cohort) => {
    if (cohort.is_enrolled) {
      return {
        text: 'Enrolled',
        icon: <CheckCircle className="h-4 w-4 text-green-500" />,
        color: 'bg-green-50 border-green-200',
        canRequest: false
      };
    }
    if (cohort.has_pending_request) {
      return {
        text: 'Pending Approval',
        icon: <Clock className="h-4 w-4 text-yellow-500" />,
        color: 'bg-yellow-50 border-yellow-200',
        canRequest: false
      };
    }
    return {
      text: 'Available',
      icon: <BookOpen className="h-4 w-4 text-blue-500" />,
      color: 'bg-white border-gray-200',
      canRequest: true
    };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isRequired ? 'Select a Class to Join' : 'Request to Join Additional Classes'}
          </DialogTitle>
          <DialogDescription>
            {isRequired
              ? 'You need to join a class to access tests. Choose from the available classes below.'
              : 'Browse available classes and request to join. Your teacher will approve your request.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading available classes...</p>
          </div>
        ) : cohorts.length === 0 ? (
          <div className="text-center py-8">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No classes available at this time.</p>
            <p className="text-sm text-gray-500 mt-2">Please contact your teacher or administrator.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cohorts.map((cohort) => {
              const status = getCohortStatus(cohort);

              return (
                <Card key={cohort.id} className={`${status.color} border-2`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          {cohort.name}
                          {status.icon}
                        </h3>
                        {cohort.description && (
                          <p className="text-sm text-gray-600 mt-1">{cohort.description}</p>
                        )}
                      </div>
                      <div className="ml-4 text-right">
                        <div className="text-xs text-gray-500 flex items-center justify-end gap-1">
                          <Users className="h-3 w-3" />
                          {cohort.student_count} students
                        </div>
                      </div>
                    </div>

                    {cohort.teachers && cohort.teachers.length > 0 && (
                      <div className="text-sm text-gray-600 mb-3">
                        <span className="font-medium">Teacher(s):</span>{' '}
                        {cohort.teachers.map(t => t.name).join(', ')}
                      </div>
                    )}

                    {status.canRequest && (
                      <div className="mt-3 space-y-2">
                        <Textarea
                          placeholder="Optional: Add a message for your teacher..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          className="text-sm h-20"
                        />
                        <Button
                          onClick={() => handleRequestEnrollment(cohort.id)}
                          disabled={requestingCohortId === cohort.id}
                          className="w-full"
                        >
                          {requestingCohortId === cohort.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Requesting...
                            </>
                          ) : (
                            'Request to Join'
                          )}
                        </Button>
                      </div>
                    )}

                    {!status.canRequest && (
                      <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600 flex items-center gap-2">
                        {status.icon}
                        <span className="font-medium">{status.text}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentModal;